<template>
  <div class="permission-analyzer">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner" />
      <p>載入權限資料中...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <p>載入權限資料失敗</p>
      <p class="error-message">{{ error }}</p>
      <button class="retry-button" @click="loadPermissions">重試</button>
    </div>

    <!-- Permission Data Display -->
    <div v-else class="permission-content">
      <!-- Search Section -->
      <div class="search-section">
        <div class="search-input-group">
          <input
            v-model="searchFeatureKey"
            type="text"
            placeholder="輸入 featureKey 進行搜尋（例如：member.list）"
            class="search-input"
            @keyup.enter="performSearch"
          >
          <button
            class="search-button"
            :disabled="!searchFeatureKey.trim()"
            @click="performSearch"
          >
            搜尋
          </button>
          <button
            v-if="searchResults"
            class="clear-button"
            @click="clearSearch"
          >
            清除
          </button>
        </div>

        <!-- Search Results -->
        <div v-if="searchResults" class="search-results">
          <div class="search-results-header">
            <h4>搜尋結果: {{ searchFeatureKey }}</h4>
            <div class="search-result-summary">
              <span
                class="result-status"
                :class="{
                  'status-allowed': searchResults.finalResult === true,
                  'status-denied': searchResults.finalResult === false,
                  'status-undefined': searchResults.finalResult === null
                }"
              >
                {{ getResultStatusText(searchResults.finalResult) }}
              </span>
            </div>
          </div>

          <div v-if="searchResults.affectedGates.length > 0" class="affected-gates">
            <h5>影響此權限的規則路徑:</h5>
            <ul class="gate-path">
              <li
                v-for="(gate, index) in searchResults.affectedGates"
                :key="index"
                class="gate-item clickable"
                @click="scrollToGate(gate)"
              >
                <span class="gate-name">{{ gate.name }}</span>
                <span class="gate-type">({{ gate.type }})</span>
                <span class="click-hint">點擊定位</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="permission-header">
        <button class="refresh-button" @click="loadPermissions">刷新</button>
      </div>

      <!-- Plan Permission Gate -->
      <div v-if="planPermissionGate" class="permission-section">
        <h3>方案權限</h3>
        <PermissionTreeNode
          :permission-gate="planPermissionGate"
          :level="0"
          :is-root="true"
          :highlight-feature-key="searchFeatureKey"
          :highlighted-gates="searchResults?.affectedGates || []"
        />
      </div>

      <!-- Role Permission Gate -->
      <div v-if="rolePermissionGate" class="permission-section">
        <h3>角色權限</h3>
        <PermissionTreeNode
          :permission-gate="rolePermissionGate"
          :level="0"
          :is-root="true"
          :highlight-feature-key="searchFeatureKey"
          :highlighted-gates="searchResults?.affectedGates || []"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, nextTick, provide } from 'vue'
import { GetPermissionList, PermissionGate } from '@/api/permissionGate'
import { useShop } from '@/use/shop'
import PermissionTreeNode from './PermissionTreeNode.vue'

export default defineComponent({
  name: 'PermissionAnalyzer',
  components: {
    PermissionTreeNode,
  },
  props: {
    testRoute: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['queryFeature'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const loading = ref(false)
    const error = ref(null)
    const planPermissionGate = ref(null)
    const rolePermissionGate = ref(null)
    const searchFeatureKey = ref('')
    const searchResults = ref(null)

    const loadPermissions = async () => {
      loading.value = true
      error.value = null

      try {
        const [res, err] = await GetPermissionList({ shopId: shopId.value })

        if (err) {
          console.error('載入權限資料失敗:', err)
          error.value = err.msg || err || '載入權限資料失敗'
          return
        }

        // 使用 PermissionGate.deserialize 解析資料
        if (res.planPermissionGate) {
          planPermissionGate.value = PermissionGate.deserialize(res.planPermissionGate)
        }

        if (res.rolePermissionGate) {
          rolePermissionGate.value = PermissionGate.deserialize(res.rolePermissionGate)
        }
      } catch (err) {
        console.error('載入權限資料失敗:', err)
        error.value = err.response?.data?.message || err.message || '未知錯誤'
      } finally {
        loading.value = false
      }
    }

    const performSearch = () => {
      const featureKey = searchFeatureKey.value.trim()
      if (!featureKey) {
        searchResults.value = null
        return
      }

      const results = {
        featureKey,
        finalResult: null,
        affectedGates: [],
        planResult: null,
        roleResult: null,
      }

      // 檢查方案權限
      if (planPermissionGate.value) {
        const [planResult, planGates] = planPermissionGate.value.check(featureKey)
        console.log('Plan Check planGates:', planGates)
        results.planResult = planResult
        if (planGates.length > 0) {
          results.affectedGates.push(...planGates)
        }
      }

      // 檢查角色權限
      if (rolePermissionGate.value) {
        const [roleResult, roleGates] = rolePermissionGate.value.check(featureKey)
        results.roleResult = roleResult
        if (roleGates.length > 0) {
          results.affectedGates.push(...roleGates)
        }
      }

      // 決定最終結果 (角色權限優先於方案權限)
      if (results.roleResult !== null) {
        results.finalResult = results.roleResult
      } else if (results.planResult !== null) {
        results.finalResult = results.planResult
      }

      searchResults.value = results
    }

    const clearSearch = () => {
      searchFeatureKey.value = ''
      searchResults.value = null
    }

    const getResultStatusText = (result) => {
      if (result === true) return '允許'
      if (result === false) return '拒絕'
      return '未定義'
    }

    const scrollToGate = async (targetGate) => {
      // 先關閉所有展開的節點（重置展開狀態）
      await collapseAllNodes()

      // 等待 DOM 更新
      await nextTick()

      // 展開所有相關的節點
      await expandGateHierarchy(targetGate)

      // 等待 DOM 更新後再進行捲動
      await nextTick()

      // 優先尋找高亮的 featureKey 項目
      let targetElement = null

      // 如果有搜尋的 featureKey，先尋找相關的 feature key 項目
      if (searchFeatureKey.value) {
        targetElement = findFeatureKeyElement(targetGate, searchFeatureKey.value)
      }

      // 如果找不到 featureKey 項目，再尋找 gate 節點
      if (!targetElement) {
        targetElement = findGateElement(targetGate)
      }

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })

        // 添加臨時的特殊高亮效果
        // targetElement.classList.add('scroll-highlight')
        // setTimeout(() => {
        //   targetElement.classList.remove('scroll-highlight')
        // }, 2000)
      }
    }

    const expandGateHierarchy = async (targetGate) => {
      // 通過事件通知所有 PermissionTreeNode 組件展開到目標節點
      const event = new CustomEvent('expandToGate', {
        detail: { targetGate },
      })
      document.dispatchEvent(event)
    }

    const collapseAllNodes = async () => {
      // 通過事件通知所有 PermissionTreeNode 組件關閉所有節點
      const event = new CustomEvent('collapseAllNodes')
      document.dispatchEvent(event)
    }

    // 處理查詢 featureKey 事件
    const onQueryFeature = (featureKey) => {
      console.log('here onQueryFeature', featureKey)
      emit('query-feature', featureKey)
    }

    // 使用 provide 提供 onQueryFeature 方法給子組件
    provide('onQueryFeature', onQueryFeature)

    const findFeatureKeyElement = (targetGate, featureKey) => {
      // 尋找在特定 gate 中的高亮 featureKey 項目
      const gateElements = document.querySelectorAll('.permission-tree-node')

      for (const gateElement of gateElements) {
        const nameElement = gateElement.querySelector('.node-name')
        if (nameElement && nameElement.textContent.trim() === targetGate.name) {
          // 在此 gate 下尋找高亮的 feature key
          const featureElements = gateElement.querySelectorAll('.feature-key-item')

          for (const featureElement of featureElements) {
            const featureKeyElement = featureElement.querySelector('.feature-key')
            if (featureKeyElement) {
              const featureKeyText = featureKeyElement.textContent.trim()

              // 直接匹配
              if (featureKeyText === featureKey) {
                return featureElement
              }

              // 通配符匹配（如果 featureKeyText 是通配符樣式）
              if (featureKeyText.endsWith('*')) {
                const prefix = featureKeyText.slice(0, -1)
                if (featureKey.startsWith(prefix)) {
                  return featureElement
                }
              }

              // 反向匹配（如果搜尋的 featureKey 符合某個通配符規則）
              if (featureKey.startsWith(featureKeyText.replace('*', ''))) {
                return featureElement
              }
            }
          }
        }
      }

      return null
    }

    const findGateElement = (targetGate) => {
      // 尋找具有對應 gate 名稱的 DOM 元素
      const elements = document.querySelectorAll('.permission-tree-node')
      for (const element of elements) {
        const nameElement = element.querySelector('.node-name')
        if (nameElement && nameElement.textContent.trim() === targetGate.name) {
          // 檢查是否為高亮的元素（更精確的匹配）
          if (element.querySelector('.highlighted-gate') ||
              element.classList.contains('highlighted-gate') ||
              nameElement.closest('.node-header').classList.contains('highlighted-gate')) {
            return element
          }
          // 如果沒有高亮元素，返回第一個匹配的元素
          return element
        }
      }
      return null
    }

    onMounted(async () => {
      await loadPermissions()
    })

    return {
      loading,
      error,
      planPermissionGate,
      rolePermissionGate,
      searchFeatureKey,
      searchResults,
      loadPermissions,
      performSearch,
      clearSearch,
      getResultStatusText,
      scrollToGate,
      findFeatureKeyElement,
      collapseAllNodes,
    }
  },
})
</script>

<style lang="postcss" scoped>
.permission-analyzer {
  @apply text-white;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  text-align: center;
  color: #58f993;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #374151;
  border-top: 4px solid #58f993;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  text-align: center;
  @apply bg-gray-800 border border-red-600;
  border-radius: 8px;
  margin: 20px 0;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-message {
  @apply text-red-400;
  margin: 8px 0;
  font-size: 14px;
}

.retry-button {
  @apply bg-blue-600 text-white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 16px;
  transition: background-color 0.2s;
}

.retry-button:hover {
  @apply bg-blue-700;
}

.permission-content {
  margin-top: 20px;
}

.search-section {
  margin-bottom: 24px;
  padding: 20px;
  @apply bg-gray-800 border border-gray-700;
  border-radius: 8px;
}

.search-input-group {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.search-input {
  flex: 1;
  padding: 10px 16px;
  @apply border-2 border-gray-600 bg-gray-700 text-white;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.search-input:focus {
  @apply border-blue-500;
}

.search-input::placeholder {
  @apply text-gray-400;
}

.search-button {
  @apply bg-blue-600 text-white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.search-button:hover:not(:disabled) {
  @apply bg-blue-700;
}

.search-button:disabled {
  @apply bg-gray-600 text-gray-400;
  cursor: not-allowed;
}

.clear-button {
  @apply bg-gray-600 text-white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.clear-button:hover {
  @apply bg-gray-700;
}

.search-results {
  margin-top: 16px;
  padding: 16px;
  @apply bg-gray-900 border border-gray-600;
  border-radius: 6px;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  @apply border-b border-gray-600;
}

.search-results-header h4 {
  margin: 0;
  @apply text-[#58f993];
  font-size: 16px;
}

.result-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-allowed {
  @apply bg-green-900 text-green-300 border border-green-600;
}

.status-denied {
  @apply bg-red-900 text-red-300 border border-red-600;
}

.status-undefined {
  @apply bg-yellow-900 text-yellow-300 border border-yellow-600;
}

.affected-gates h5 {
  margin: 0 0 12px 0;
  @apply text-gray-300;
  font-size: 14px;
}

.gate-path {
  list-style: none;
  padding: 0;
  margin: 0;
}

.gate-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  @apply bg-gray-800 border border-gray-600;
  border-radius: 4px;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.gate-item.clickable {
  cursor: pointer;
}

.gate-item.clickable:hover {
  @apply bg-gray-700 border-blue-500;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(52, 152, 219, 0.1);
}

.click-hint {
  font-size: 11px;
  @apply text-gray-400 bg-gray-600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: auto;
}

.gate-item.clickable:hover .click-hint {
  @apply text-blue-300 bg-gray-700;
}

.gate-name {
  font-weight: 500;
  @apply text-white;
}

.gate-type {
  font-size: 12px;
  @apply text-gray-400 bg-gray-600;
  padding: 2px 8px;
  border-radius: 12px;
}

.permission-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  @apply border-b-2 border-gray-600;
}

.permission-header h2 {
  margin: 0;
  @apply text-[#58f993];
}

.refresh-button {
  @apply bg-green-600 text-white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.refresh-button:hover {
  @apply bg-green-700;
}

.permission-section {
  margin-bottom: 32px;
  @apply bg-gray-800 border border-gray-700;
  border-radius: 8px;
  padding: 16px;
}

.permission-section h3 {
  margin: 0 0 16px 0;
  @apply text-[#58f993];
  font-size: 18px;
}
</style>
